import React, { HTMLAttributes, useEffect } from "react";
import { Checkbox, CheckboxModes, Input } from "@trussle/external-components";
import cn from "classnames";
import Joi from "joi";
import { Link } from "gatsby";
import { getReferrerFromCookie } from "../../../../helpers/landingParams";
import { useFormContext } from "../context/FormContext";
import { CTA } from "./CTA";
import { ErrorMessage } from "./ErrorMessage";
import { MagicLink, Signup } from "../../../../api/auth";
import { HomeBuyingStage, MortgagePurpose } from "../../../../types/common";
import { Spinner } from "../../../Spinner";
import { Intent } from "../../../../api/auth/types";
import {
  CustomerType, FF2EventName, trackFF2Event, trackSignup
} from "../analytics";
import { submitPurchaseData, submitRemoData } from "../api/publicApiCrmContactClient";
import { FormData as FormDataType } from "../types";

function removeCommas(str: string) {
  return str.replace(/,/g, "");
}

const schema = Joi.object({
  firstName: Joi.string().required().messages({
    "string.empty": "First name is required"
  }),
  phoneNumber: Joi.string().required().messages({
    "string.empty": "Phone number is required"
  }),
  email: Joi.string()
    .email({ tlds: { allow: false } })
    .required()
    .messages({
      "string.empty": "Email is required",
      "string.email": "Email must be a valid email"
    })
});

type ISignupFormProps = HTMLAttributes<HTMLFormElement>;

const mapIntentToCustomerType = (intent: Intent): CustomerType => {
  switch (intent) {
    case Intent.PURCHASE_OFFER_MADE:
      return CustomerType.PURCHASE;
    case Intent.REMO:
      return CustomerType.REMORTGAGE;
    case Intent.PURCHASE_RESEARCH:
      return CustomerType.MIP;
    case Intent.PURCHASE_VIEWING:
      return CustomerType.PURCHASE;
    default:
      throw new Error("Unknown intent");
  }
}

const getIntent = (formData: FormDataType) => {
  let intent = null;

  if (
    formData.mortgagePurpose === MortgagePurpose.Purchase
    || formData.mortgagePurpose === MortgagePurpose.PurchaseToLet
  ) {
    switch (formData.homeBuyingStage) {
      case HomeBuyingStage.Researching:
        intent = Intent.PURCHASE_RESEARCH;
        break;
      case HomeBuyingStage.Viewing:
        intent = Intent.PURCHASE_VIEWING;
        break;
      case HomeBuyingStage.Accepted:
      default:
        intent = Intent.PURCHASE_OFFER_MADE;
    }
  } else if (formData.mortgagePurpose === MortgagePurpose.Remo) {
    intent = Intent.REMO;
  } else {
    intent = Intent.PURCHASE_OFFER_MADE;
  }

  return intent;
};

export const SignupForm: React.FC<ISignupFormProps> = ({ className, ...rest }) => {
  const { formData, setFormData, recalculateStep } = useFormContext();
  const [errors, setErrors] = React.useState<Joi.ValidationError | null>(null);
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [checkboxState, setCheckboxState] = React.useState({
    emailCheckbox: false,
    phoneCheckbox: false,
    postCheckbox: false,
  });

  useEffect(() => {
    const intent = getIntent(formData)

    if (intent === Intent.PURCHASE_VIEWING) {
      trackFF2Event(FF2EventName.REDIRECT_SIGNUP_MIP, formData);
    } else if (intent === Intent.REMO) {
      trackFF2Event(FF2EventName.REDIRECT_SIGNUP_REMO, formData);
    } else {
      trackFF2Event(FF2EventName.REDIRECT_SIGNUP_PURCHASE, formData);
    }
  }, [])

  useEffect(() => {
    recalculateStep()
  }, [formData])

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setCheckboxState((prevState) => ({
      ...prevState,
      [id]: checked,
    }));
  };
  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors(null);
    setApiError(null);

    const inputs = new FormData(e.currentTarget);
    const inputEntries = Object.fromEntries(inputs.entries());

    const { error } = schema.validate(inputEntries, { abortEarly: false });

    if (error) {
      setErrors(error);
    } else {
      setErrors(null);

      const intent = getIntent(formData)

      setIsSubmitting(true);

      const email = inputEntries.email as string;
      const firstName = inputEntries.firstName as string;
      const phoneNumber = inputEntries.phoneNumber as string;
      const referrer = getReferrerFromCookie();
      if (intent === Intent.REMO) {
        await submitRemoData(
          firstName,
          email,
          {
            email: checkboxState.emailCheckbox
          },
          formData,
          referrer,
        );
      } else {
        await submitPurchaseData(
          firstName,
          email,
          {
            email: checkboxState.emailCheckbox
          },
          formData,
          referrer,
        );
      }

      const response = await Signup({
        email,
        firstName,
        phoneNumber,
        subscriptions: {
          email: checkboxState.emailCheckbox,
          phone: checkboxState.phoneCheckbox,
          post: checkboxState.postCheckbox
        },
        intent,
        income: formData.combinedIncome
          ? parseInt(removeCommas(formData.combinedIncome!), 10)
          : undefined,
        propertyValue: formData.propertyValue
          ? parseInt(removeCommas(formData.propertyValue!), 10)
          : undefined,
        depositSize: formData.deposit
          ? parseInt(removeCommas(formData.deposit!), 10)
          : undefined,
        lookingForAdditionalBorrowing: formData.additionalBorrowing === "yes",
        productEndDate: formData.dealExpiry ? new Date(formData.dealExpiry) : undefined,
        balance: formData.balance
          ? parseInt(removeCommas(formData.balance!), 10)
          : undefined,
        isLowContact: false,
        offerAccepted: formData.homeBuyingStage === HomeBuyingStage.Accepted,
        purchasingPurpose: formData.mortgagePurpose
      });

      if (response.ok) {
        await trackSignup(
          mapIntentToCustomerType(intent),
          email,
          firstName,
          phoneNumber,
          response.applicantId
        );

        const applicationUrl = `${FACTFIND_HOST}/profile/application`;
        const affordabilityJourneyUrl = `${FACTFIND_HOST}/profile/affordability-journey`;

        if (intent === Intent.PURCHASE_VIEWING) {
          window.location.assign(affordabilityJourneyUrl);
        } else {
          window.location.assign(applicationUrl);
        }
      } else if (response.error === "This email address is already registered. Please try logging in.") {
        const response = await MagicLink(email)
        setIsSubmitting(false);

        if (response.ok) {
          setFormData({
            ...formData,
            magicLinkSent: true
          })
        } else {
          setApiError("An internal error occurred. Please try again.")
        }
      } else {
        setIsSubmitting(false);
        setApiError(response.error || "An error occurred. Please try again.");
      }
    }
  };

  return (
    <form {...rest} className={cn("heroff2-form", className)} onSubmit={onSubmit}>
      <h2>View your personalised mortgage deals</h2>
      <ul className="heroff2-form-list">
        <li>Speak to a fee-free adviser</li>
        <li>Submit your application online</li>
        <li>Get rate-change alerts as soon as they happen</li>
      </ul>
      <Input name="firstName" variant="normal" label="First name" />
      <Input name="phoneNumber" variant="normal" label="Phone number" />
      <Input name="email" variant="email" label="Email" />
      <div className="heroff2-form-checkboxes">
        <Checkbox
          checked={checkboxState.emailCheckbox}
          onChange={handleCheckboxChange}
          classOverrides={{ label: "heroff2-form-checkbox-label" }}
          mode={CheckboxModes.LightV4}
          id="emailCheckbox"
          label="Email"
        />
        <Checkbox
          checked={checkboxState.phoneCheckbox}
          onChange={handleCheckboxChange}
          classOverrides={{ label: "heroff2-form-checkbox-label" }}
          mode={CheckboxModes.LightV4}
          id="phoneCheckbox"
          label="Phone"
        />
        <Checkbox
          checked={checkboxState.postCheckbox}
          onChange={handleCheckboxChange}
          classOverrides={{ label: "heroff2-form-checkbox-label" }}
          mode={CheckboxModes.LightV4}
          id="postCheckbox"
          label="Post"
        />
      </div>
      <p className="heroff2-form-checkboxes-note">
        Keep me up to date with the latest mortgage news and rates (you can unsubscribe at
        any time).
      </p>
      <ErrorMessage errors={errors} />
      <ErrorMessage errors={apiError} />
      {isSubmitting ? <Spinner /> : <CTA>Get Started</CTA>}
      <p className="heroff2-form-note">
        Next, we'll ask you a few more details about your mortgage goals. You can save
        progress and finish whenever you like.
        <br />
        <br />
        By tapping Get Started, you confirm you've read and accepted our
        {" "}
        <Link className="heroff2-form-link" to="/">Privacy Policy</Link>
        {" "}
        and
        {" "}
        <Link className="heroff2-form-link" to="/">Terms of Business</Link>
        .
      </p>
    </form>
  );
};
